import { makeStyles } from '@material-ui/core';
import useTheme from '@material-ui/core/styles/useTheme';
import { mapValues, reduce } from 'lodash';
import { useMemo } from 'react';

// makePaletteColor('your-metric-name', 'light-mode-color', 'dark-mode-color')

const COLORS = {
    status: {
        ...makePaletteColor('green', '#70C377', '#70C377'),
        ...makePaletteColor('gray', '#999999', '#AAAAAA'),
        ...makePaletteColor('yellow', '#FFDC75', '#FFDC75'),
        ...makePaletteColor('orange', '#FAAD4E', '#FAAD4E'),
        ...makePaletteColor('red', '#EB7861', '#EB7861'),
    },
    metrics: {
        ...makePaletteColor('networkIn', '#a069db', '#a069db'),
        ...makePaletteColor('networkOut', '#55BDE0', '#55BDE0'),
        ...makePaletteColor('radioChannelUtil', '#A069DB', '#A069DB'),
        ...makePaletteColor('serverCpu', '#5DA5DA', '#5DA5DA'),
        ...makePaletteColor('serverCpuCapacity', '#616161', '#616161'),
        ...makePaletteColor('serverCpuReady', '#666666', '#666666'),
        ...makePaletteColor('serverMemActive', '#F06292', '#F06292'),
        ...makePaletteColor('serverMemCapacity', '#616161', '#616161'),
        ...makePaletteColor('serverMemGranted', '#4CAF50', '#4CAF50'),
        ...makePaletteColor('serverDiskIn', '#70C377', '#70C377'),
        ...makePaletteColor('serverDiskOut', '#F17CB0', '#F17CB0'),
        ...makePaletteColor('serverMem', '#5DA5DA', '#5DA5DA'),
        ...makePaletteColor('serverNetIn', '#B2912F', '#B2912F'),
        ...makePaletteColor('serverNetOut', '#B276B2', '#B276B2'),
        ...makePaletteColor('serverProcesses', '#5DA5DA', '#5DA5DA'),
        ...makePaletteColor('pathLogLatency', '#7F6FC5', '#7F6FC5'),
        ...makePaletteColor('pathLogHops', '#FF0000', '#FF0000'),
        ...makePaletteColor('syslogEmergency', '#EB7861', '#EB7861'),
        ...makePaletteColor('syslogAlertCritical', '#FAAD4E', '#FAAD4E'),
        ...makePaletteColor('syslogError', '#FFDC75', '#FFDC75'),
        ...makePaletteColor('syslogWarning', '#ADD8E6', '#ADD8E6'),
        ...makePaletteColor('syslogNoticeInfoDebug', '#88d5B0', '#88d5B0'),
    },
    chart: {
        ...makePaletteColor('axis', '#F2F2F2', '#404040'),
        ...makePaletteColor('crosshair', '#555555', '#C5C5C5'),
        ...makePaletteColor('gridLines', '#F2F2F2', '#404040'),
        ...makePaletteColor('xAxisTicks', '#C5C5C5', '#404040'),
        ...makePaletteColor('yAxisTicks', '#F2F2F2', '#404040'),
        ...makePaletteColor('itemHoverStyle', '#333333', '#E0E0E3'),
        ...makePaletteColor('dataLabelBackground', '#FFFFFF', '#202020'),
    },
};

const DEFAULT_METRIC_COLORS = [
    makeDefaultPaletteColor('#7CB5EC', '#7CB5EC'),
    makeDefaultPaletteColor('#434348', '#636368'),
    makeDefaultPaletteColor('#90ED7D', '#90ED7D'),
    makeDefaultPaletteColor('#F7A35C', '#F7A35C'),
    makeDefaultPaletteColor('#8085E9', '#8085E9'),
    makeDefaultPaletteColor('#F15C80', '#F15C80'),
    makeDefaultPaletteColor('#E4D354', '#E4D354'),
    makeDefaultPaletteColor('#2B908F', '#2B908F'),
    makeDefaultPaletteColor('#F45B5B', '#F45B5B'),
    makeDefaultPaletteColor('#91E8E1', '#91E8E1'),
];

const EXTENDED_METRIC_COLORS = [
    makeDefaultPaletteColor('#67DC6A', '#67DC6A'),
    makeDefaultPaletteColor('#756FC5', '#756FC5'),
    makeDefaultPaletteColor('#947274', '#947274'),
    makeDefaultPaletteColor('#5BA3CA', '#5BA3CA'),
    makeDefaultPaletteColor('#20BEB2', '#20BEB2'),
    makeDefaultPaletteColor('#FF69B4', '#FF69B4'),
    makeDefaultPaletteColor('#4CAF50', '#4CAF50'),
    makeDefaultPaletteColor('#673AB7', '#673AB7'),
    makeDefaultPaletteColor('#795548', '#795548'),
    makeDefaultPaletteColor('#2196F3', '#2196F3'),
    makeDefaultPaletteColor('#009688', '#009688'),
    makeDefaultPaletteColor('#F92E83', '#F92E83'),
    makeDefaultPaletteColor('#388E3C', '#388E3C'),
    makeDefaultPaletteColor('#512DA8', '#512DA8'),
    makeDefaultPaletteColor('#5D4037', '#5D4037'),
    makeDefaultPaletteColor('#3F51B5', '#3F51B5'),
    makeDefaultPaletteColor('#00796B', '#00796B'),
    makeDefaultPaletteColor('#B2185B', '#B2185B'),
    makeDefaultPaletteColor('#607D9B', '#607D9B'),
    makeDefaultPaletteColor('#455A84', '#455A84'),
];

/**
 * Exports the colors as either a light or dark variant to be applied
 * to the overall MUI theme
 * @param {String} paletteType
 */
export function exportPaletteType(paletteType) {
    return mapValues(COLORS, (root) => mapValues(root, (paletteTypes) => paletteTypes[paletteType]));
}

/**
 * Returns the color for a status given a palette type
 *
 * @param {String} status The status name green/gray/etc...
 * @param {String} paletteType light/dark
 */
export function getStatusColor(status, paletteType) {
    if (!status) return undefined;
    validateThemePaletteType(paletteType);

    const statusColor = COLORS.status[status];
    if (!statusColor) {
        return status;
    }

    return statusColor[paletteType];
}

/**
 * Returns an object containing all of the status colors for a given palette type
 *
 * @param {String} paletteType
 */
export function getStatusColors(paletteType) {
    validateThemePaletteType(paletteType);

    return mapValues(COLORS.status, (paletteTypes) => paletteTypes[paletteType]);
}

/**
 * Returns the color for a given metric given a palette type
 *
 * @param {String} metric The status name green/gray/etc...
 * @param {String} paletteType light/dark
 */
export function getMetricColor(metric, paletteType) {
    validateThemePaletteType(paletteType);

    return COLORS.metrics[metric][paletteType];
}

/**
 * Returns the color for a given metric index given a palette type
 * @param {Number} index The metric index
 * @param {String} paletteType light/dark
 * @returns {String}
 */
export function getDefaultMetricColor(index, paletteType) {
    validateThemePaletteType(paletteType);

    return DEFAULT_METRIC_COLORS[index % DEFAULT_METRIC_COLORS.length][paletteType];
}

/**
 * Returns the extended color for a given metric index given a palette type. Currently
 * used for CBQoS, as we support up to 20 distinct class colors.
 * @param {Number} index The metric index
 * @param {String} paletteType light/dark
 * @returns {String}
 */
export function getExtendedMetricColor(index, paletteType) {
    validateThemePaletteType(paletteType);

    return EXTENDED_METRIC_COLORS[index % EXTENDED_METRIC_COLORS.length][paletteType];
}

/**
 * Returns the color for a status given the current theme variant
 *
 * This is a react hook. Do not call this function conditionally.
 *
 * @param {String} status The status name green/gray/etc...
 */
export function useStatusColor(status) {
    const paletteType = usePaletteType();

    return getStatusColor(status, paletteType);
}

/**
 * Returns an object of the status colors for the current theme variant
 *
 * This is a react hook. Do not call this function conditionally.
 */
export function useStatusColors() {
    const paletteType = usePaletteType();

    return useMemo(() => getStatusColors(paletteType), [paletteType]);
}

/**
 * Returns an object with CSS classes used for styles.
 *
 * Example Usage:
 *
 *      // where status is "green", "gray", etc...
 *      function MyComponent({ status }) {
 *          const statusClasses = useStatusColorStyles();
 *
 *          return <div className={clsx(statusClasses.statusColor, status || 'gray')} />;
 *      }
 *
 * @param {{
 *      styleProp: import('csstype').StandardProperties
 * }} [options]
 */
export function useStatusColorStyles(options) {
    const { styleProp = 'backgroundColor' } = options || {};
    const colors = useStatusColors();

    const useStyles = useMemo(
        () =>
            makeStyles({
                statusColor: reduce(
                    colors,
                    (classes, color, name) => {
                        classes[`&.${name}`] = {
                            [styleProp]: color,
                        };

                        return classes;
                    },
                    {}
                ),
            }),
        [colors, styleProp]
    );

    return useStyles();
}

/**
 * Returns the color for a metric given the current theme variant
 *
 * This is a react hook. Do not call this function conditionally.
 *
 * @param {String} metric The metric name
 */
export function useMetricColor(metric) {
    const paletteType = usePaletteType();

    return getMetricColor(metric, paletteType);
}

/**
 * React hook to return the current theme type (light/dark)
 */
export function usePaletteType() {
    const theme = useTheme();

    return theme.palette.type; // light or dark
}

function validateThemePaletteType(variant) {
    if (variant !== 'light' && variant !== 'dark') {
        throw new Error(`Invalid variant value: ${variant}`);
    }
}

function makePaletteColor(status, lightColor, darkColor) {
    return {
        [status]: {
            light: lightColor,
            dark: darkColor,
        },
    };
}

function makeDefaultPaletteColor(light, dark) {
    return {
        light,
        dark,
    };
}

export function getChartColor(key, paletteType) {
    validateThemePaletteType(paletteType);
    return COLORS.chart[key][paletteType];
}

/**
 * Given a hex color string (#000000) lightens each component by the amount specified
 *
 * TODO: Evaluate how well this works for dark mode (initially used for comparison overlay chart where it might be fine)
 *
 * @param {String} color
 * @param {Number} percent
 * @return {String}
 */
export function lightenColor(color, percent) {
    // go from #111 to #111111
    if (/^#[0-9A-F]{3}$/i.test(color)) {
        color = `#${color[1]}${color[1]}${color[2]}${color[2]}${color[3]}${color[3]}`;
    }

    // make sure this is a valid color
    if (!/^#[0-9A-F]{6}$/i.test(color)) {
        return color;
    }

    const hex = parseInt(color.slice(1), 16);
    // eslint-disable-next-line no-bitwise
    const rgb = [hex >> 16, (hex & 0x00ff00) >> 8, hex & 0x0000ff];
    const amount = Math.round(255 * percent);

    const rgbString = rgb
        .map((c) => {
            let str = Math.max(0, Math.min(255, c + amount)).toString(16);

            if (str.length === 1) {
                str = `0${str}`;
            }

            return str;
        })
        .join('');
    return `#${rgbString}`;
}
